import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Divider, useTheme } from "@mui/material";
import { useRecommendationDetails } from "../../../RecommendationDetailsProvider";
import { TwoColLayout } from "../TwoColLayout";
import { BreakdownBlock } from "./components/BreakdownBlock";
import { ShowMoreTypography } from "./components/ShowMoreTypography";
import { getHoldingsChartData } from "./utils";
import { BreakdownTable } from "./components/BreakdownTable";

export const HoldingsSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { investment, fundDetails, investmentFundDetails, logos, names } =
    useRecommendationDetails();
  const [showMore, setShowMore] = useState(false);
  const [hoveredId, setHoveredId] = useState<string>();

  const holdingsBreakdown = useMemo(
    () => fundDetails?.breakdowns?.holding || [],
    [fundDetails?.breakdowns?.holding]
  );
  const investmentHoldingsBreakdown = useMemo(
    () => investmentFundDetails?.breakdowns?.holding || [],
    [investmentFundDetails?.breakdowns?.holding]
  );

  const chartData = useMemo(
    () => getHoldingsChartData(holdingsBreakdown, theme),
    [holdingsBreakdown, theme]
  );

  const chartDataInvestment = useMemo(
    () => getHoldingsChartData(investmentHoldingsBreakdown, theme),
    [investmentHoldingsBreakdown, theme]
  );

  const total = investment?.currentMarketValueAC ?? 0;

  if (
    holdingsBreakdown.length === 0 &&
    investmentHoldingsBreakdown.length === 0
  ) {
    return null;
  }

  return (
    <Box pb={!showMore ? 0 : 11}>
      <TwoColLayout
        title={t("recommender.compareView.donutCharts.top10Holdings")}
        marginBottom={6}
        component1={
          <BreakdownBlock
            data={chartDataInvestment}
            name={names.investment}
            logo={logos.originalFund}
            total={total}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
          />
        }
        component2={
          <BreakdownBlock
            data={chartData}
            name={names.recommendation}
            logo={logos.recommendation}
            total={total}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
          />
        }
      />
      {!showMore && (
        <ShowMoreTypography showMore={showMore} setShowMore={setShowMore} />
      )}
      <Collapse in={showMore}>
        <TwoColLayout
          component1={
            <BreakdownTable
              name={names.investment}
              items={chartDataInvestment}
            />
          }
          component2={
            <BreakdownTable name={names.recommendation} items={chartData} />
          }
        />
      </Collapse>
      {showMore && (
        <ShowMoreTypography
          showMore={showMore}
          setShowMore={setShowMore}
          marginTop={8}
        />
      )}
      {!showMore && <Divider sx={{ mt: 9, mb: 8 }} />}
    </Box>
  );
};
